import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'src/app/store';
import { exceptionStatusByExceptionType, EXCEPTION_STATUS } from 'src/constants/ELDConstants';
import { Exception } from '../hosLog/types';

import { HosLogState } from './hosLogStore';
import { normalizeHosLog } from '../hosEditLog/utils';

const getState = (state: RootState): HosLogState => state.hosLog;

export const getIsEditingForms = createSelector(
  getState,
  (state: HosLogState) => state.isEditingForms,
);
export const getIsAddingException = createSelector(
  getState,
  (state: HosLogState) => state.isAddingException,
);
export const getIsInsertingDutyStatus = createSelector(
  getState,
  (state: HosLogState) => state.isInsertingDutyStatus,
);
export const getHosForms = createSelector(getState, (state: HosLogState) => state.hosForms);
export const getHosDaily = createSelector(getState, (state: HosLogState) => state.hosDaily);
export const getFcaDate = createSelector(getState, (state: HosLogState) => state.fcaDate);
export const getTimelineData = createSelector(getState, (state: HosLogState) => state.timelineData);
export const getHosLogs = createSelector(getState, (state: HosLogState) => state.hosLogs);
export const getHosLog = createSelector(getState, (state: HosLogState) => state.selectedHosLog);
export const getHosLogNormalized = createSelector(getState, (state: HosLogState) => {
  return normalizeHosLog(state.selectedHosLog);
});

export const getHosLogsWithViolations = createSelector(getState, (state: HosLogState) =>
  state.hosLogs?.filter(log => log?.violationCount > 0),
);
export const getHosLogsWithPendingEdits = createSelector(getState, (state: HosLogState) =>
  state.hosLogs?.filter(
    log => log?.editActivity != null && log?.editActivity?.status === 'pending',
  ),
);

// TODO: This (HosLogsWithMissingInfo) doesn't have a definition yet so we return an empty array until the definition is determined
export const getHosLogsWithMissingInfo = createSelector(getState, (state: HosLogState) => []);

export const getHosLogsUncertified = createSelector(getState, (state: HosLogState) =>
  state.hosLogs?.filter(log => log?.certifiedStatus == 0),
);

export const getCurrentDayHosLog = createSelector(
  getState,
  (state: HosLogState) => state.currentDayHosLog,
);
export const getEditingDutyStatus = createSelector(
  getState,
  (state: HosLogState) => state.selectedEditingStatus,
);
export const getHosLogTimeZone = createSelector(
  getState,
  (state: HosLogState) => state.selectedHosLog.timeZone,
);
export const getHosFilter = createSelector(getState, (state: HosLogState) => state.hosFilter);
export const getHosFilterFormState = createSelector(
  getState,
  (state: HosLogState) => state.hosFilterFormState,
);
export const getHosRangeFilter = createSelector(
  getState,
  (state: HosLogState) => state.hosRangeFilter,
);
export const getHosExportJobId = createSelector(
  getState,
  (state: HosLogState) => state.hosExportJobId,
);
export const getHosExportLink = createSelector(
  getState,
  (state: HosLogState) => state.hosExportLink,
);

export const getUserLocations = createSelector(
  getState,
  (state: HosLogState) => state.userLocations,
);

export const getRawExceptions = createSelector(getHosLog, hosLog => hosLog.exceptions || []);
export const getExceptionsWitTypes = createSelector(getRawExceptions, exceptions =>
  exceptions.filter(
    item =>
      item.exceptionType === EXCEPTION_STATUS.AD || item.exceptionType === EXCEPTION_STATUS.SH,
  ),
);

export const getPendingExceptions = createSelector(getState, state => state.pendingExceptions);
export const getPendingRemovalExceptions = createSelector(
  getState,
  state => state.pendingRemovalExceptions,
);

export const getExceptions = createSelector(
  getExceptionsWitTypes,
  getPendingRemovalExceptions,
  (exceptions, removalExceptions) => {
    return exceptions.map((exception: Exception) =>
      removalExceptions.find(
        item => exception.exceptionType === exceptionStatusByExceptionType[item.exceptionType],
      )
        ? { ...exception, canceled: true }
        : exception,
    );
  },
);

export const getPendingEdits = createSelector(getState, (state: HosLogState) => state.pendingEdits);

export const getActiveViolationId = createSelector(
  getState,
  (state: HosLogState) => state.activeViolationId,
);

export const getCalendarHosLogs = createSelector(
  getState,
  (state: HosLogState) => state.calendarHosLogs,
);

export const getHosLogExemptStatus = createSelector(
  getState,
  (state: HosLogState) => state.selectedHosLog.exemptStatus,
);

export const getUnsavedHOSLogEditsPresent = createSelector(
  getState,
  (state: HosLogState) => state.hosLogEdits.unsavedHOSLogEditsPresent,
);

export const getUnsavedHOSExceptionsEditsPresent = createSelector(
  getState,
  (state: HosLogState) => state.hosLogEdits.unsavedHOSExceptionsEditsPresent,
);
